/**
 * LoginPage - one login form for both students and chore posters
 * --------------------------------------------------------------
 * The server works out which kind of account it is, so people do not have to
 * remember which button they signed up with.
 *
 * MODERATORS LOG IN IN TWO STEPS
 * A moderator can read reported conversations between an adult and a child, so
 * a password on its own is not enough for that account. When the server
 * recognises one it sends a six-digit code to their inbox and asks for it here
 * before letting them in. Everybody else sees exactly what they always did -
 * this screen does not know who is a moderator until the server says so, and
 * it has no business knowing before then.
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Mail, ArrowLeft, Clock } from 'lucide-react';

import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import BanNotice from '../components/BanNotice';

import { useI18n } from '../hooks/useI18n';
import { useResendCooldown } from '../hooks/useResendCooldown';
import PasswordInput from '../components/PasswordInput';

export default function LoginPage() {
  const { t } = useI18n();
  const { login, finishTwoFactorLogin, endedSession, clearEndedSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Set only once the password has been accepted and a code has been sent.
  // Held here rather than saved anywhere: a half-finished login should not
  // survive a page reload, and it stops existing the moment they leave.
  // Set when a correct password belongs to a banned account - see BanNotice.
  const [ban, setBan] = useState(null);

  /**
   * There are TWO ways to arrive at the ban screen, and both end up here.
   *
   *   1. They typed a correct password into a banned account - `ban` above.
   *   2. They were already signed in and a moderator banned them while they
   *      sat there. useAuth notices, empties the session and sends them here
   *      carrying the reason - `endedSession.ban`.
   *
   * The second one is why this is not just local state. Without it somebody
   * banned mid-session was silently logged out with no explanation at all,
   * and had to guess that logging in again was how to find out why.
   */
  const shownBan = ban || endedSession?.ban || null;
  const [pending, setPending] = useState(null); // { challenge, sentTo, expiresInMinutes }
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState('');

  /**
   * THIRTY SECONDS HERE, NOT THE TWO MINUTES USED EVERYWHERE ELSE.
   *
   * A moderator's code dies after ten minutes, so a two-minute wait to ask
   * for another would spend a fifth of its life on the wait - and this is the
   * account that already has the most friction on the way in. Thirty seconds
   * is what the server enforces anyway (TWO_FACTOR_RESEND_SECONDS in
   * config/features.js); the countdown just means somebody sees the wait
   * rather than meeting it as a red error.
   *
   * Not keyed by account: nobody is logged in yet, and the challenge is
   * short-lived enough that carrying a wait between two attempts is right.
   */
  const codeCooldown = useResendCooldown('two-factor', 30);

  // If they were sent here from a private page, go back there afterwards.
  const destination = location.state?.from || '/dashboard';

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);

    try {
      const result = await login(form);

      if (result.twoFactorRequired) {
        setPending(result);
        setCode('');
        return; // stay here, second step below
      }

      navigate(destination, { replace: true });
    } catch (err) {
      // A banned account is not an error message - it is a screen. The
      // password was right; what they need is the reason and how long is
      // left, not a red line under a form they cannot use.
      if (err.code === 'ACCOUNT_SUSPENDED' && err.ban) {
        setBan(err.ban);
        return;
      }
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleCodeSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setBusy(true);

    try {
      await finishTwoFactorLogin(pending.challenge, code);
      navigate(destination, { replace: true });
    } catch (err) {
      // The ten minutes ran out, so there is nothing to type a code into any
      // more. Send them back to the password rather than leaving them
      // retyping a code that cannot work whatever they do.
      if (err.code === 'CHALLENGE_EXPIRED') {
        setPending(null);
        setCode('');
        setError(t('twoFactor.tooSlow'));
      } else {
        setError(err.message);
        setCode('');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      const again = await authApi.resendTwoFactor(pending.challenge);
      setNotice(again.message || 'A new code is on its way.');
      codeCooldown.start();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startAgain = () => {
    setPending(null);
    setCode('');
    setError('');
    setNotice('');
  };

  // ---------------------------------------------------------------------
  // BANNED - the password was right and they still do not get in
  // ---------------------------------------------------------------------
  if (shownBan) {
    return (
      <div className="auth-layout">
        <BanNotice
          ban={shownBan}
          // The countdown reached zero while they sat here. Put the form
          // back rather than making them work out that a reload might help.
          // Both sources are cleared: whichever one is not showing is
          // already null, so clearing both is safe and clearing one is not.
          onFinished={() => {
            setBan(null);
            clearEndedSession();
          }}
        />
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // STEP TWO - the emailed code
  // ---------------------------------------------------------------------
  if (pending) {
    return (
      <div className="auth-layout">
        <div className="page-header text-center">
          <h1>{t('login.checkEmail')}</h1>
          <p>
            {t('twoFactor.sentTo', { email: pending.sentTo, minutes: pending.expiresInMinutes || 10 })}
          </p>
        </div>

        <div className="card">
          <form className="form" onSubmit={handleCodeSubmit}>
            {error && <div className="alert alert-error">{error}</div>}
            {notice && <div className="alert alert-success">{notice}</div>}

            <div className="field">
              <label htmlFor="code">{t('login.yourCode')}</label>
              <input
                id="code"
                name="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                /* Not type="number": a code with a leading zero is a real code,
                   and number inputs happily eat it. inputMode brings up the
                   digit keypad on a phone without changing what is allowed,
                   which matters because a recovery code goes in this box too. */
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                placeholder="123456"
                className="code-input"
              />
              <span className="hint">
                {t('twoFactor.noCode')}
              </span>
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              <LogIn size={18} />
              {busy ? t('twoFactor.checking') : t('login.submit')}
            </button>
          </form>

          <hr className="divider" />

          <div className="row-between">
            <button type="button" className="btn btn-outline btn-sm btn-inline" onClick={startAgain}>
              <ArrowLeft size={16} />
              {t('twoFactor.startAgain')}
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm btn-inline"
              onClick={handleResend}
              disabled={busy || !codeCooldown.ready}
            >
              {codeCooldown.ready ? <Mail size={16} /> : <Clock size={16} />}
              {codeCooldown.ready
                ? t('twoFactor.sendNewCode')
                : t('resend.waitFor', { time: codeCooldown.label })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // STEP ONE - email and password, exactly as before
  // ---------------------------------------------------------------------
  return (
    <div className="auth-layout">
      <div className="page-header text-center">
        <h1>{t('login.title')}</h1>
        <p>{t('login.subtitle')}</p>
      </div>

      <div className="card">
        <form className="form" onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}

          <div className="field">
            <label htmlFor="email">{t('login.email')}</label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </div>

          <div className="field">
            <div className="row-between" style={{ gap: 'var(--space-xs)' }}>
              <label htmlFor="password">{t('login.password')}</label>
              <Link to="/forgot-password" className="text-sm">
                {t('login.forgot')}
              </Link>
            </div>
            <PasswordInput
              id="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
            />
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            <LogIn size={18} />
            {busy ? t('common.loading') : t('login.submit')}
          </button>
        </form>

        <hr className="divider" />

        <p className="text-center text-sm text-muted">
          {t('login.noAccount')} <Link to="/signup">{t('login.signUp')}</Link>
        </p>
      </div>
    </div>
  );
}
